import type { Address, PaymentInformation } from '~/types/account'
import {
  PayPalFunding,
  type Invoice,
  type InvoiceConfirmation,
  type UpdatePaymentMethodPayload,
  type UpdateRecurlyPaymentMethodPayload
} from '~/types/checkout'
import BaseProxyController from '~/service/base'
import { getStampinupApiDomain } from '~/utils/locale'
import type { CheckoutProperties } from '~/types/persistent'
import { addCommonQueryParam } from '~/utils/general'
import { useCheckoutStore } from '~/stores/checkout'
import { useLocaleStore } from '~/stores/locale'
import { useRootStore } from '~/stores/root'
import { useTypedRuntimeConfig } from '~/utils/runtimeConfig'

export abstract class CheckoutServiceBase {
  BASE_URL = 'checkout'

  protected getInvoiceUrl () {
    return `${this.BASE_URL}/invoice`
  }

  async fetchInvoice () {
    const query = addCommonQueryParam()
    return await $api()<Invoice>(this.getInvoiceUrl(), { query })
  }

  async updateShippingAddress (address: Address) {
    const query = addCommonQueryParam()
    const invoice = await $api()<Invoice>('checkout/invoice/address', { body: { ...address }, query, method: 'PATCH' })
    return invoice
  }

  async updateShippingMethod (shippingMethodId: number) {
    const query = addCommonQueryParam()
    query.shippingMethodId = shippingMethodId.toString()
    const invoice = await $api()<Invoice>('checkout/invoice/shipping', { query, method: 'PATCH' })
    return invoice
  }

  async updatePaymentMethod ({ payment = null, authorizationId = null, catalogMailingId = undefined, isSubscription = false }: UpdatePaymentMethodPayload, invoice: Invoice | null) {
    let url = `${this.BASE_URL}/invoice/payment`
    if (catalogMailingId) {
      url = `catalog-mailing/checkout/invoice/${catalogMailingId}/payment`
    } else if (isSubscription) {
      // The checkout store sets this whenever a subscription invoice is active
      // (stores/checkout.ts) — without the branch a subscription payment update
      // patches the regular cart invoice instead. AB#63254.
      url = 'subscriptions/checkout/invoice/payment'
    }
    const localeStore = useLocaleStore()
    const $config = useTypedRuntimeConfig()
    const baseURL = getStampinupApiDomain($config.public.API_PREFIX, localeStore.culture, $config.public.API_PORT)
    const urlWithParams = new URL(url, baseURL)

    const query = addCommonQueryParam()
    let body: Partial<PaymentInformation> = {}

    if (payment !== null) {
      body = payment
    } else if (authorizationId) {
      urlWithParams.searchParams.append('paymentAuthorizationId', authorizationId)
    }
    if (urlWithParams.searchParams.toString().length > 0) {
      url = `${url}${urlWithParams.search}`
    }

    // temp fix until backend can track the paypal funding
    // Only use authorizationId as funding when it's a known funding source string,
    // not when it's an actual PayPal authorization UUID
    const isFundingSource = authorizationId && Object.values(PayPalFunding).includes(authorizationId as PayPalFunding)
    const paypalFunding = (isFundingSource ? authorizationId : null) ?? invoice?.paypalFunding ?? null
    const newInvoice = await $api()<Invoice>(url, { body, query, method: 'PATCH' })
    return paypalFunding ? { ...newInvoice, paypalFunding } : newInvoice
  }

  async updateRecurlyPaymentMethod (payload: UpdateRecurlyPaymentMethodPayload, recaptchaToken: string, recaptchaAction?: string) {
    let url = `${this.BASE_URL}/invoice/payment/recurly`
    if (payload.catalogMailingId) {
      url = `catalog-mailing/checkout/invoice/${payload.catalogMailingId}/payment/recurly`
    }
    if (payload.isSubscription) {
      if (payload.subscriptionId) {
        url = `account/subscriptions/payment/${payload.subscriptionId}`
      } else {
        url = 'subscriptions/checkout/invoice/payment/recurly'
      }
    }
    const localeStore = useLocaleStore()
    const $config = useTypedRuntimeConfig()
    const baseURL = getStampinupApiDomain($config.public.API_PREFIX, localeStore.culture, $config.public.API_PORT)
    const urlWithParams = new URL(url, baseURL)

    if (payload.saveToProfile) {
      urlWithParams.searchParams.append('saveToProfile', payload.saveToProfile.toString())
    }

    if (payload.recurlyToken) {
      urlWithParams.searchParams.append('recurlyToken', payload.recurlyToken)
      if (payload.isPaypal) {
        urlWithParams.searchParams.append('paymentAuthorizationId', 'paypal')
      }
    }
    if (payload.billingInfoId) {
      urlWithParams.searchParams.append('billingInfoId', payload.billingInfoId)
    }

    const rootStore = useRootStore()
    if (rootStore.signifydSessionId) {
      urlWithParams.searchParams.append('signifydSessionId', rootStore.signifydSessionId)
      if (rootStore.clientIpAddress) {
        urlWithParams.searchParams.append('clientIpAddress', rootStore.clientIpAddress)
      }
    }

    // Attach the token when we have one and send the request either way. Whether a
    // tokenless request is acceptable is the backend's call -- a second frontend gate
    // here could disagree with the backend flag and fail the payment locally on an
    // endpoint that was never going to verify it. AB#63069.
    if (recaptchaToken) {
      urlWithParams.searchParams.append('recaptcha_token', recaptchaToken)
      if (recaptchaAction) {
        urlWithParams.searchParams.append('recaptcha_action', recaptchaAction)
      }
    }

    const query = addCommonQueryParam()

    if (urlWithParams.searchParams.toString().length > 0) {
      url = `${url}${urlWithParams.search}`
    }

    const body = rootStore.signifydSessionId && payload.cardDetails ? { ...payload.cardDetails } : undefined
    const invoice = await $api()<Invoice>(url, { query, method: 'PATCH', ...(body && { body }) })
    return invoice
  }

  async submit (checkoutProperties?: CheckoutProperties) {
    const query = addCommonQueryParam()
    const invoice = await $api()<Invoice>(`${this.BASE_URL}/invoice`, { body: checkoutProperties, query, method: 'POST' })
    if (this.BASE_URL === 'join/checkout') {
      // needed on front end because of the toggle feature. Once the toggle is removed, this can be moved to the backend
      // don't wait for this to finish. If it fails, they will get the popup when they sign in to their demo account
      $api()('loyalty/opt-in/?enable=true', {
        method: 'PATCH'
      })
    }
    const checkoutStore = useCheckoutStore()
    if (!invoice.recurlyWants3DSecure) {
      await checkoutStore.fetchConfirmationAfterOrder(invoice.invoiceId)
    }
    return invoice
  }

  async fetchConfirmationInvoice (invoiceId: string) {
    const invoice = await $api()<InvoiceConfirmation>(`${this.BASE_URL}/invoice/confirmation/${invoiceId}`)
    return invoice
  }
}

export class UserCheckoutService extends CheckoutServiceBase {
}

export class JoinCheckoutService extends CheckoutServiceBase {
  override BASE_URL = 'join/checkout'
}

/* istanbul ignore next */
export default () => {
  const checkoutProxyController = new BaseProxyController({
    UserService: UserCheckoutService,
    JoinService: JoinCheckoutService,
  })
  const proxy = new Proxy<UserCheckoutService>({} as unknown as UserCheckoutService, checkoutProxyController)
  return proxy
}
